import { useState, useRef, useCallback, useEffect } from "react";
import _ from "lodash";
import { ElementComponentData } from "data/form/elementSelectors";
import { ElementData } from "records/Element";
import { produce } from "immer";

type UpdateProps = { id: string; value: string | number };

export default function (
	callback: (
		currentData: ElementComponentData<string | number>,
		nextData: ElementData<string | number>
	) => void,
	items: UpdateProps[]
) {
	// Pending value is one that is currently being sync'd to the API
	// We use it as our "currentValue" if we need to prep another update
	// before the previous value is finished sync'ing to the API
	const previousData = useRef(JSON.stringify(items));

	const updateRefs = useCallback((update, index) => {
		if (previousData) {
			try {
				const prev = JSON.parse(previousData.current);
				prev[index] = update;
				previousData.current = JSON.stringify(prev);
			} catch (err) {
				console.error("There was a problem updating current data", err);
			}
		}
	}, []);

	const update = useRef(
		_.debounce((current, next, index) => {
			const curr = JSON.parse(current)[index];
			callback(curr, next);
			updateRefs(next, index);
		}, 1000)
	);

	const [_items, setItems] = useState(items);

	const onChange = useCallback(
		(nextData: { id: string; value: string | number }, index: number) => {
			const newItems = produce(_items, draft => {
				// If the index exists - merge the changes so we don't lose
				// any feedback / status
				if (draft[index] !== undefined) {
					draft[index] = { ...nextData };
				} else {
					draft[index] = nextData;
				}
			});

			setItems(newItems);
			update.current(previousData.current, nextData, index);
		},
		[_items]
	);

	const flushChanges = useCallback(() => {
		update.current.flush();
	}, []);

	useEffect(() => {
		const timer = update;
		const prev = previousData;

		// If our data is updated externally
		if (JSON.stringify(items) !== previousData.current) {
			// If we have no local change update our local data
			if (JSON.stringify(_items) === previousData.current) {
				setItems(items);
				// Update our prevData
				prev.current = JSON.stringify(items);
			}
		}

		// Flush any changes before
		// we unmount
		return () => {
			timer.current.flush();
		};
		// disabled - false warning - this is the behaviour we require
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items]);

	return {
		items: _items || [],
		onChange,
		onBlur: flushChanges,
		flushChanges,
	};
}
